/* =========================================================
   User manager (ETC Admin).

   General account list filterable by organization, site and
   role, with create/edit and per-account sign-in methods.
   Which methods are available is decided by the role's auth
   policy: ETC Admin always has 2FA, instructors never do,
   Microsoft SSO is optional.

   Passwords can only be *set*, never read back.
   ========================================================= */
import React, { useReducer, useState } from 'react';
import type {
  AppUser, AuthMethodPolicy, AuthMethods, ManagedRole, UserDraft, UserPatch, UserStatus,
} from '@/types';
import { ic, sized, type IconElement } from '@/components/Icons';
import { BLUE_HEADER, DataTable, Modal, Pagination, type Column } from '@/components/ui';
import { AUTH_POLICY, ds } from '@/data';
import { useI18n } from '@/i18n';
import { paginate, sortRows, useTableState, type SortMap } from '@/hooks/useTableState';
import { useAppStore } from '@/store/useAppStore';

const ROLE_LIST: ManagedRole[] = ['ETC_ADMIN', 'PARTNER_MGR', 'SITE_MGR', 'SITE_ADMIN'];

interface Method {
  key: keyof AuthMethods;
  icon: IconElement;
  es: string;
  en: string;
}

const METHODS: Method[] = [
  { key: 'password', icon: ic.key, es: 'Contraseña', en: 'Password' },
  { key: 'twoFactor', icon: ic.shield, es: 'Verificación 2 pasos', en: 'Two-step (2FA)' },
  { key: 'microsoft', icon: ic.microsoft, es: 'Microsoft SSO', en: 'Microsoft SSO' },
];

/** Métodos que el Admin ETC no puede ENCENDER desde aquí, por mucho que el
 *  interruptor se deje mover.
 *
 *  La verificación en dos pasos nace de escanear un código con el teléfono:
 *  el secreto se genera en ese momento y solo la propia persona puede
 *  hacerlo, desde su cuenta. Microsoft todavía no está conectado en este
 *  portal. Apagarlos sí está en su mano —eso es retirar algo que ya
 *  existe—, así que el bloqueo solo aplica cuando están en «no».
 *
 *  Vitalino lo probó (10.6): activaba el interruptor, guardaba, y la cuenta
 *  seguía sin doble factor. El servidor lo explicaba en un aviso, pero para
 *  entonces la persona ya había dado el paso en falso. */
const SOLO_LA_PERSONA: Partial<Record<keyof AuthMethods, { es: string; en: string }>> = {
  twoFactor: {
    es: 'La activa la propia persona desde su cuenta, escaneando el código.',
    en: 'The account holder turns this on from their own account, by scanning the code.',
  },
  microsoft: {
    es: 'Todavía no está conectado en este portal.',
    en: 'Not connected in this portal yet.',
  },
};

const ROLE_TONE: Record<string, string> = {
  ETC_ADMIN: 'var(--brand-primary-d)',
  PARTNER_MGR: 'var(--st-read)',
  SITE_MGR: 'var(--st-edit)',
  SITE_ADMIN: 'var(--st-edit)',
};

/* ---------- password generation + strength ---------- */

function rnd(n: number) {
  if (window.crypto?.getRandomValues) {
    const a = new Uint32Array(1);
    window.crypto.getRandomValues(a);
    return a[0] % n;
  }
  return Math.floor(Math.random() * n);
}

export function genPassword(len = 16) {
  const U = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const L = 'abcdefghijkmnpqrstuvwxyz';
  const D = '23456789';
  const S = '!@#$%^&*-_=+?';
  const all = U + L + D + S;
  const pick = (set: string) => set[rnd(set.length)];
  const out = [pick(U), pick(L), pick(D), pick(S)];
  for (let i = out.length; i < len; i++) out.push(pick(all));
  for (let i = out.length - 1; i > 0; i--) {
    const j = rnd(i + 1);
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out.join('');
}

export function scorePassword(pw: string) {
  if (!pw) return 0;
  let s = 0;
  if (pw.length >= 8) s++;
  if (pw.length >= 12) s++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return Math.min(s, 4);
}

function PasswordSection({
  value, hasPassword, passwordSetAt, required, onChange,
}: {
  value: string;
  hasPassword: boolean;
  passwordSetAt?: string | null;
  /** Cuenta nueva: sin contraseña no podría entrar. */
  required?: boolean;
  onChange: (v: string) => void;
}) {
  const { lang } = useI18n();
  const [show, setShow] = useState(false);
  const [copied, setCopied] = useState(false);
  const score = scorePassword(value);

  const meta = [
    { l: lang === 'es' ? 'Muy débil' : 'Very weak', c: 'var(--err)' },
    { l: lang === 'es' ? 'Débil' : 'Weak', c: 'var(--err)' },
    { l: lang === 'es' ? 'Aceptable' : 'Fair', c: 'var(--warn)' },
    { l: lang === 'es' ? 'Buena' : 'Good', c: 'var(--brand-primary)' },
    { l: lang === 'es' ? 'Fuerte' : 'Strong', c: 'var(--ok)' },
  ][score];

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = value;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
      } catch {
        /* clipboard unavailable */
      }
      document.body.removeChild(ta);
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  }

  return (
    <div style={{ marginTop: 16, padding: 14, border: '1px solid var(--line)', borderRadius: 10, background: 'var(--surface-2)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ color: 'var(--brand-primary)', display: 'inline-flex' }}>{sized(ic.key, 16)}</span>
        <div style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--text)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
          {lang === 'es' ? 'Contraseña' : 'Password'}
        </div>
        {hasPassword ? (
          <span className="chip lock" style={{ marginLeft: 'auto' }}>
            {lang === 'es' ? 'Configurada' : 'Set'}{passwordSetAt ? ' · ' + passwordSetAt : ''}
          </span>
        ) : (
          <span className="chip warn" style={{ marginLeft: 'auto' }}>{lang === 'es' ? 'Sin definir' : 'Not set'}</span>
        )}
      </div>

      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', marginTop: 6, fontSize: 11.5, color: 'var(--text-3)' }}>
        <span style={{ color: 'var(--info)', flexShrink: 0, display: 'inline-flex' }}>{sized(ic.shield, 13)}</span>
        <span>
          {lang === 'es'
            ? 'Por seguridad, las contraseñas actuales no se pueden consultar. Aquí solo se asigna una nueva.'
            : 'For security, current passwords cannot be viewed. You can only assign a new one.'}
        </span>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <input
            type={show ? 'text' : 'password'}
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
              setCopied(false);
            }}
            autoComplete="new-password"
            placeholder={lang === 'es' ? 'Escribe o genera una contraseña' : 'Type or generate a password'}
            style={{
              padding: '8px 36px 8px 10px', border: '1px solid var(--line-2)', borderRadius: 6, fontSize: 13,
              background: 'var(--surface)', width: '100%',
              fontFamily: value ? 'var(--mono)' : 'inherit',
              letterSpacing: value && !show ? '0.12em' : 'normal',
            }}
          />
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            aria-label={show ? (lang === 'es' ? 'Ocultar' : 'Hide') : lang === 'es' ? 'Ver' : 'Show'}
            style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', cursor: 'pointer', color: show ? 'var(--brand-primary)' : 'var(--text-3)', display: 'inline-flex', padding: 4 }}
          >
            {sized(ic.eye, 16)}
          </button>
        </div>
        <button
          type="button"
          className="btn"
          onClick={() => {
            onChange(genPassword(16));
            setShow(true);
            setCopied(false);
          }}
        >
          {sized(ic.refresh, 14)} {lang === 'es' ? 'Generar' : 'Generate'}
        </button>
        <button type="button" className="btn" disabled={!value} onClick={copy}>
          {copied ? sized(ic.check, 14) : null} {copied ? (lang === 'es' ? 'Copiada' : 'Copied') : lang === 'es' ? 'Copiar' : 'Copy'}
        </button>
      </div>

      <div style={{ marginTop: 10 }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} style={{ flex: 1, height: 5, borderRadius: 99, background: value && i < score ? meta.c : 'var(--line-2)', transition: 'background .2s ease' }} />
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5, fontSize: 11 }}>
          <span style={{ color: value ? meta.c : 'var(--text-3)', fontWeight: 700 }}>
            {value ? meta.l : lang === 'es' ? 'Se recomiendan 12+ caracteres' : '12+ characters recommended'}
          </span>
          {value ? <span style={{ color: 'var(--text-3)' }}>{value.length} {lang === 'es' ? 'caracteres' : 'chars'}</span> : null}
        </div>
      </div>

      {value && value.length < MIN_PASSWORD ? (
        <div style={{ marginTop: 8, fontSize: 11.5, color: 'var(--err)', display: 'flex', gap: 6, alignItems: 'center' }}>
          {sized(ic.warn, 13)}
          {lang === 'es'
            ? `Debe tener al menos ${MIN_PASSWORD} caracteres.`
            : `It must be at least ${MIN_PASSWORD} characters.`}
        </div>
      ) : null}

      {required && !value ? (
        <div style={{ marginTop: 8, fontSize: 11.5, color: 'var(--warn)', display: 'flex', gap: 6, alignItems: 'flex-start' }}>
          {sized(ic.warn, 13)}
          <span>
            {lang === 'es'
              ? 'Obligatoria: todavía no hay envío de correo, así que la contraseña se entrega a mano. Se le pedirá cambiarla al entrar.'
              : 'Required: email delivery is not available yet, so the password is handed over directly. They will be asked to change it on first sign-in.'}
          </span>
        </div>
      ) : null}

      {value ? (
        <div style={{ marginTop: 8, fontSize: 11.5, color: 'var(--warn)', display: 'flex', gap: 6, alignItems: 'center' }}>
          {sized(ic.warn, 13)}
          {lang === 'es' ? 'Cópiala ahora: no podrás verla después de guardar.' : 'Copy it now: you won’t be able to see it after saving.'}
        </div>
      ) : null}
    </div>
  );
}

/** Forces the methods the role's policy requires or forbids. */
function reconcileAuth(role: ManagedRole, auth?: Partial<AuthMethods>): AuthMethods {
  const pol = AUTH_POLICY[role];
  const fix = (m: 'twoFactor' | 'microsoft') =>
    pol[m] === 'required' ? true : pol[m] === 'off' ? false : !!auth?.[m];
  return { password: pol.password !== 'off', twoFactor: fix('twoFactor'), microsoft: fix('microsoft') };
}

function MethodIcons({ auth }: { auth: AuthMethods }) {
  return (
    <div style={{ display: 'flex', gap: 9 }}>
      {METHODS.map((m) => (
        <span key={m.key} data-tip={m.es} style={{ color: auth[m.key] ? 'var(--brand-primary)' : 'var(--line-2)', display: 'inline-flex' }}>
          {sized(m.icon, 16)}
        </span>
      ))}
    </div>
  );
}

function StatusChip({ status }: { status: UserStatus }) {
  const { lang } = useI18n();
  if (status === 'active') return <span className="chip ok">{lang === 'es' ? 'Activo' : 'Active'}</span>;
  if (status === 'invited') return <span className="chip warn">{lang === 'es' ? 'Invitado' : 'Invited'}</span>;
  return <span className="chip lock">{lang === 'es' ? 'Inactivo' : 'Disabled'}</span>;
}

function Avatar({ u, size = 30 }: { u: Pick<AppUser, 'FirstName' | 'LastName'>; size?: number }) {
  const initials = ((u.FirstName || '?')[0] + ((u.LastName || '')[0] ?? '')).toUpperCase();
  return (
    <span
      style={{
        width: size, height: size, borderRadius: 99, background: 'var(--brand-primary-l)',
        color: 'var(--brand-primary-d)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 700, fontSize: size * 0.38, flexShrink: 0,
      }}
    >
      {initials}
    </span>
  );
}

function MethodToggle({
  method, value, policyState, onChange,
}: {
  method: Method;
  value: boolean;
  policyState?: AuthMethodPolicy;
  onChange: (v: boolean) => void;
}) {
  const { lang } = useI18n();
  const es = lang === 'es';
  const porPolitica = policyState === 'required' || policyState === 'off' || policyState === undefined;
  // Encenderlo no depende de este formulario; apagarlo sí.
  const ajeno = !value && !!SOLO_LA_PERSONA[method.key];
  const locked = porPolitica || ajeno;
  const note =
    ajeno
      ? (policyState === 'required'
          ? (es ? 'Obligatorio · pendiente de configurar' : 'Required · not set up yet')
          : (es ? SOLO_LA_PERSONA[method.key]!.es : SOLO_LA_PERSONA[method.key]!.en))
    : policyState === 'required' ? (es ? 'Obligatorio' : 'Required')
    : policyState === 'off' ? (es ? 'No disponible' : 'Not available')
    : null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', border: '1px solid var(--line)', borderRadius: 8, background: value ? 'var(--brand-primary-l)' : 'var(--surface-2)' }}>
      <span style={{ color: value ? 'var(--brand-primary-d)' : 'var(--text-3)', display: 'inline-flex' }}>{sized(method.icon, 18)}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{es ? method.es : method.en}</div>
        {note ? <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 1, lineHeight: 1.3 }}>{note}</div> : null}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        aria-label={lang === 'es' ? method.es : method.en}
        disabled={locked}
        onClick={() => !locked && onChange(!value)}
        style={{
          width: 40, height: 22, borderRadius: 99, border: 'none', position: 'relative', flexShrink: 0,
          background: value ? 'var(--brand-primary)' : 'var(--line-2)', cursor: locked ? 'not-allowed' : 'pointer',
          opacity: locked ? 0.7 : 1, transition: 'background .15s ease',
        }}
      >
        <span style={{ position: 'absolute', top: 2, left: value ? 20 : 2, width: 18, height: 18, borderRadius: 99, background: '#fff', boxShadow: 'var(--shadow-1)', transition: 'left .15s ease' }} />
      </button>
    </div>
  );
}

/* ---------- create / edit ---------- */

type Editable = Omit<AppUser, 'id' | 'orgId' | 'siteId'> & {
  id?: string;
  orgId: string;
  siteId: string;
  newPassword: string;
};

function seedForm(u?: Partial<AppUser> | null): Editable {
  const base: Partial<AppUser> =
    u && u.id
      ? { ...u }
      : { FirstName: '', LastName: '', Email: '', role: 'PARTNER_MGR', orgId: '', siteId: '', status: 'invited', lastActive: null };
  return {
    FirstName: base.FirstName ?? '',
    LastName: base.LastName ?? '',
    Email: base.Email ?? '',
    role: (base.role ?? 'PARTNER_MGR') as ManagedRole,
    status: (base.status ?? 'invited') as UserStatus,
    lastActive: base.lastActive ?? null,
    passwordSetAt: base.passwordSetAt ?? null,
    id: base.id,
    orgId: base.orgId ?? '',
    siteId: base.siteId ?? '',
    auth: reconcileAuth((base.role ?? 'PARTNER_MGR') as ManagedRole, base.auth),
    newPassword: '',
  };
}

/** Lo mismo que anuncia el medidor y lo que exige el servidor. */
const MIN_PASSWORD = 12;

/* Etiquetas y cajas del formulario.
 *
 *  `L` vive AQUÍ, fuera de `UserEditor`, y no es un detalle de estilo.
 *  Estaba definida dentro del cuerpo del componente, así que cada render
 *  creaba una función nueva; React veía un tipo de componente distinto en
 *  esa posición y desmontaba el subárbol entero para volver a montarlo.
 *  Con un `<input>` dentro, el efecto era que al escribir el campo se
 *  destruía en cada tecla: el foco saltaba a `<body>`, el cursor volvía a
 *  la posición cero y el nombre salía escrito al revés («onilatiV» al
 *  teclear «Vitalino»). Dar de alta una cuenta era imposible.
 *
 *  Una función de componente definida dentro de otro componente y usada
 *  como etiqueta JSX siempre hace esto. Cuando no hay campos de texto
 *  dentro no se nota, y por eso pasa desapercibido. */
const lbl: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, color: 'var(--text-2)',
  textTransform: 'uppercase', letterSpacing: '.04em',
};

const inp: React.CSSProperties = {
  padding: '8px 10px', border: '1px solid var(--line-2)', borderRadius: 6,
  fontSize: 13, background: 'var(--surface)', width: '100%',
};

function L({ children, span }: { children: React.ReactNode; span?: boolean }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, gridColumn: span ? 'span 2' : 'auto' }}>
      {children}
    </label>
  );
}

function UserEditor({
  open, onClose, user, onSave, saving,
}: {
  open: boolean;
  onClose: () => void;
  user: Partial<AppUser> | null;
  onSave: (u: UserPatch & { id?: string }) => void;
  saving: boolean;
}) {
  const { lang, t } = useI18n();
  const isNew = !user?.id;
  const [f, setF] = useState<Editable>(() => seedForm(user));

  React.useEffect(() => {
    setF(seedForm(user));
  }, [user, open]);

  const set = (patch: Partial<Editable>) => setF((s) => ({ ...s, ...patch }));
  // El gerente del socio ve todos los sites de su organización: no lleva
  // site propio (ver server/src/scope.ts).
  const setRole = (role: ManagedRole) => setF((s) => ({
    ...s, role, auth: reconcileAuth(role, s.auth), siteId: role === 'PARTNER_MGR' ? '' : s.siteId,
  }));
  const esGerente = f.role === 'PARTNER_MGR';

  const orgs = ds.listOrgs();
  const sitesForOrg = f.orgId ? ds.listSites({ orgId: f.orgId }) : [];
  const pol = AUTH_POLICY[f.role];

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.Email);
  // El servidor también lo rechaza, pero enterarse allí es tarde y el
  // mensaje llega como un aviso suelto encima de la pantalla. Aquí sale
  // debajo del campo, mientras se escribe, y dice de quién es el correo.
  const dueño = f.Email.trim()
    ? ds.listUsers().find((u) =>
        u.Email.toLowerCase() === f.Email.trim().toLowerCase() && u.id !== user?.id)
    : undefined;
  const repetido = !!dueño;
  // Una cuenta nueva nace con contraseña: todavía no hay envío de correo
  // para que la persona se la ponga ella, así que sin esto no podría entrar.
  const needsPassword = isNew && pol.password !== 'off';
  const passwordOk = !f.newPassword || f.newPassword.length >= MIN_PASSWORD;
  const valid =
    !!f.FirstName.trim() && !!f.LastName.trim() && emailOk && !repetido && passwordOk &&
    (!needsPassword || f.newPassword.length >= MIN_PASSWORD);


  return (
    <Modal
      open={open}
      onClose={onClose}
      width={620}
      headerStyle={BLUE_HEADER}
      title={isNew ? (lang === 'es' ? 'Nuevo usuario' : 'New user') : lang === 'es' ? 'Editar usuario' : 'Edit user'}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={saving}>{t('common.cancel')}</button>
          <button
            className="btn primary"
            disabled={!valid || saving}
            onClick={() => {
              const { newPassword, ...rest } = f;
              const out: UserPatch & { id?: string } = {
                ...rest,
                orgId: f.orgId || null,
                siteId: f.siteId || null,
              };
              // Viaja solo hacia el servidor, que la guarda como hash. Ni se
              // conserva en el navegador ni vuelve nunca en una respuesta.
              if (newPassword) out.password = newPassword;
              onSave(out);
            }}
          >
            {ic.save}{' '}
            {saving
              ? (lang === 'es' ? 'Guardando…' : 'Saving…')
              : isNew ? (lang === 'es' ? 'Crear usuario' : 'Create user') : t('common.save')}
          </button>
        </>
      }
    >
      <div className="m-grid1" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <L>
          <span style={lbl}>{lang === 'es' ? 'Nombre' : 'First name'}</span>
          <input style={inp} value={f.FirstName} onChange={(e) => set({ FirstName: e.target.value })} />
        </L>
        <L>
          <span style={lbl}>{lang === 'es' ? 'Apellido' : 'Last name'}</span>
          <input style={inp} value={f.LastName} onChange={(e) => set({ LastName: e.target.value })} />
        </L>
        <L span>
          <span style={lbl}>Email</span>
          <input
            style={{ ...inp, borderColor: f.Email && (!emailOk || repetido) ? 'var(--err)' : 'var(--line-2)' }}
            value={f.Email}
            onChange={(e) => set({ Email: e.target.value })}
            placeholder="nombre@dominio.com"
          />
          {f.Email && !emailOk ? (
            <span style={{ fontSize: 11, color: 'var(--err)' }}>
              {lang === 'es' ? 'Escribe un correo válido, con @ y dominio.' : 'Enter a valid email address.'}
            </span>
          ) : repetido ? (
            <span style={{ fontSize: 11, color: 'var(--err)' }}>
              {lang === 'es'
                ? `Ese correo ya es de la cuenta de ${dueño!.FirstName} ${dueño!.LastName}. Cada cuenta necesita el suyo.`
                : `That email already belongs to ${dueño!.FirstName} ${dueño!.LastName}. Each account needs its own.`}
            </span>
          ) : null}
        </L>

        <L>
          <span style={lbl}>{lang === 'es' ? 'Rol' : 'Role'}</span>
          <select style={inp} value={f.role} onChange={(e) => setRole(e.target.value as ManagedRole)}>
            {ROLE_LIST.map((r) => (
              <option key={r} value={r}>{t('roles.' + r)}</option>
            ))}
          </select>
        </L>
        <L>
          <span style={lbl}>{lang === 'es' ? 'Estado' : 'Status'}</span>
          <select style={inp} value={f.status} onChange={(e) => set({ status: e.target.value as UserStatus })}>
            <option value="active">{lang === 'es' ? 'Activo' : 'Active'}</option>
            <option value="invited">{lang === 'es' ? 'Invitado' : 'Invited'}</option>
            <option value="disabled">{lang === 'es' ? 'Inactivo' : 'Disabled'}</option>
          </select>
        </L>

        <L>
          <span style={lbl}>{lang === 'es' ? 'Organización' : 'Organization'}</span>
          <select style={inp} value={f.orgId} onChange={(e) => set({ orgId: e.target.value, siteId: '' })}>
            <option value="">{lang === 'es' ? '— Ninguna / Todas —' : '— None / All —'}</option>
            {orgs.map((o) => (
              <option key={o.OrgId} value={o.OrgId}>{o.OrgName}</option>
            ))}
          </select>
        </L>
        <L>
          <span style={lbl}>Site</span>
          <select style={inp} value={esGerente ? '' : f.siteId} disabled={!f.orgId || esGerente} onChange={(e) => set({ siteId: e.target.value })}>
            <option value="">
              {esGerente
                ? (lang === 'es' ? '— Todos los de la organización —' : '— All in the organization —')
                : (lang === 'es' ? '— Ninguno —' : '— None —')}
            </option>
            {sitesForOrg.map((s) => (
              <option key={s.SiteId} value={s.SiteId}>{s.SiteName}</option>
            ))}
          </select>
        </L>
      </div>

      <div style={{ marginTop: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 8 }}>
          {lang === 'es' ? 'Métodos de inicio de sesión' : 'Sign-in methods'}
        </div>
        <div className="m-grid1" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
          {METHODS.map((m) => (
            <MethodToggle
              key={m.key}
              method={m}
              value={f.auth[m.key]}
              policyState={pol[m.key]}
              onChange={(v) => set({ auth: { ...f.auth, [m.key]: v } })}
            />
          ))}
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 8, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
          <span style={{ color: 'var(--info)', flexShrink: 0, display: 'inline-flex' }}>{sized(ic.info, 13)}</span>
          <span>
            {lang === 'es'
              ? 'Los métodos disponibles dependen del tipo de usuario. El admin ETC exige 2FA. '
                + 'La verificación en dos pasos la activa cada persona desde su propia cuenta; '
                + 'desde aquí solo se puede retirar.'
              : 'Available methods depend on the user type. ETC admin requires 2FA. '
                + 'Two-step verification is turned on by each person from their own account; '
                + 'from here it can only be removed.'}
          </span>
        </div>
      </div>

      {pol.password !== 'off' ? (
        <PasswordSection
          value={f.newPassword}
          hasPassword={!isNew && !!f.auth?.password}
          passwordSetAt={f.passwordSetAt}
          required={needsPassword}
          onChange={(v) => set({ newPassword: v })}
        />
      ) : null}
    </Modal>
  );
}

/* ---------- the manager ---------- */

type Row = AppUser & { _key: string };

export function UserManager() {
  const { lang, t } = useI18n();
  const addToast = useAppStore((s) => s.addToast);
  const [, force] = useReducer((x: number) => x + 1, 0);

  const [q, setQ] = useState('');
  const [orgId, setOrgId] = useState('');
  const [siteId, setSiteId] = useState('');
  const [roleF, setRoleF] = useState('');
  const [editing, setEditing] = useState<Partial<AppUser> | null>(null);
  const [saving, setSaving] = useState(false);
  const table = useTableState(10);

  const orgs = ds.listOrgs();
  const orgName = (oid: string | null) => orgs.find((o) => o.OrgId === oid)?.OrgName;
  const siteName = (sid: string | null) => (sid ? ds.getSite(sid)?.SiteName : undefined);
  const sitesForFilter = orgId ? ds.listSites({ orgId }) : ds.listSites().slice(0, 80);

  const all = ds.listUsers({
    q,
    orgId: orgId || undefined,
    siteId: siteId || undefined,
    role: roleF || undefined,
  });

  const enriched: Row[] = all.map((u) => ({ ...u, _key: u.id }));

  const sortValues: SortMap<Row> = {
    name: { type: 'text', get: (r) => `${r.LastName || ''} ${r.FirstName || ''}`.trim() },
    role: { type: 'text', get: (r) => t('roles.' + r.role) },
    org: { type: 'text', get: (r) => orgName(r.orgId) || '' },
    auth: { type: 'number', get: (r) => Object.values(r.auth).filter(Boolean).length },
    status: { type: 'text', get: (r) => r.status || '' },
    last: { type: 'text', get: (r) => r.lastActive || '' },
  };

  const { rows, page, pageCount } = paginate(sortRows(enriched, table.sort, sortValues), table.page, table.per);

  async function save(u: UserPatch & { id?: string }) {
    setSaving(true);
    try {
      const { record, notes } = u.id
        ? await ds.updateUser(u.id, u)
        : await ds.createUser(u as UserDraft);

      addToast({
        kind: 'save',
        celebrate: true,
        title: u.id
          ? (lang === 'es' ? 'Usuario actualizado' : 'User updated')
          : (lang === 'es' ? 'Usuario creado' : 'User created'),
        body: `${record.FirstName} ${record.LastName}`,
      });

      // Lo que el servidor no pudo aplicar tal cual se pidió —el doble
      // factor lo activa cada persona, el acceso con Microsoft todavía no
      // existe— se dice, en vez de dejar el interruptor puesto mintiendo.
      for (const n of notes) {
        addToast({ kind: 'info', title: lang === 'es' ? 'Ten en cuenta' : 'Note', body: n });
      }
      setEditing(null);
    } catch (err) {
      addToast({
        kind: 'err',
        title: lang === 'es' ? 'No se guardó' : 'Not saved',
        body: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setSaving(false);
      force();
    }
  }

  const selStyle: React.CSSProperties = {
    padding: '7px 10px', borderRadius: 7, border: '1px solid var(--line-2)',
    fontSize: 12.5, background: 'var(--surface)', maxWidth: 200,
  };

  const columns: Column<Row>[] = [
    {
      key: 'name',
      label: lang === 'es' ? 'Usuario' : 'User',
      sortable: true,
      render: (r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar u={r} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>{r.FirstName} {r.LastName}</div>
            <div className="mono" style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 1 }}>{r.Email}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'role',
      label: lang === 'es' ? 'Rol' : 'Role',
      sortable: true,
      render: (r) => (
        <span style={{ color: ROLE_TONE[r.role] || 'var(--text-2)', fontWeight: 700, fontSize: 12, letterSpacing: '.01em' }}>
          {t('roles.' + r.role)}
        </span>
      ),
    },
    {
      key: 'org',
      label: lang === 'es' ? 'Organización' : 'Organization',
      muted: true,
      sortable: true,
      render: (r) => {
        // El formulario solo enseña una organización y un site; una cuenta
        // puede cubrir varios. Decirlo aquí evita que alguien crea que a un
        // gerente con tres organizaciones le queda solo una.
        const extra = (r.scopeCount ?? 0) - (r.orgId ? 1 : 0) - (r.siteId ? 1 : 0);
        return (
          <div style={{ minWidth: 0 }}>
            <div className="truncate" style={{ maxWidth: 180 }}>{orgName(r.orgId) || '—'}</div>
            {r.siteId ? <div className="mono" style={{ fontSize: 11, color: 'var(--text-3)' }}>{siteName(r.siteId) || r.siteId}</div> : null}
            {extra > 0 ? (
              <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                {lang === 'es' ? `y ${extra} ámbito${extra > 1 ? 's' : ''} más` : `and ${extra} more scope${extra > 1 ? 's' : ''}`}
              </div>
            ) : null}
          </div>
        );
      },
    },
    { key: 'auth', label: lang === 'es' ? 'Acceso' : 'Sign-in', sortable: true, render: (r) => <MethodIcons auth={r.auth} /> },
    { key: 'status', label: lang === 'es' ? 'Estado' : 'Status', sortable: true, render: (r) => <StatusChip status={r.status} /> },
    {
      key: 'last',
      label: lang === 'es' ? 'Última actividad' : 'Last active',
      align: 'right',
      sortable: true,
      render: (r) => (
        <span className="mono" style={{ fontSize: 12, color: 'var(--text-3)' }}>
          {r.lastActive || (lang === 'es' ? 'Nunca' : 'Never')}
        </span>
      ),
    },
    {
      key: 'edit',
      label: '',
      align: 'right',
      render: (r) => (
        <button className="btn sm ghost" aria-label={t('common.edit')} onClick={(e) => { e.stopPropagation(); setEditing(r); }}>
          {ic.edit}
        </button>
      ),
    },
  ];

  return (
    <div className="card" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: '-0.01em', color: 'var(--brand-ink)' }}>
            {lang === 'es' ? 'Gestor de usuarios' : 'User manager'}
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginTop: 2 }}>
            {all.length} {lang === 'es' ? 'usuarios · crea, edita y asigna métodos de acceso' : 'users · create, edit and assign sign-in methods'}
          </div>
        </div>
        <button className="btn primary" onClick={() => setEditing({})}>
          {ic.plus} {lang === 'es' ? 'Nuevo usuario' : 'New user'}
        </button>
      </div>

      {/* filters */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }}>{ic.search}</span>
          <input
            placeholder={t('common.search')}
            value={q}
            onChange={(e) => { setQ(e.target.value); table.setPage(1); }}
            style={{ padding: '8px 12px 8px 32px', border: '1px solid var(--line-2)', borderRadius: 7, fontSize: 13, width: 220 }}
          />
        </div>
        <select
          value={orgId}
          onChange={(e) => { setOrgId(e.target.value); setSiteId(''); table.setPage(1); }}
          aria-label={lang === 'es' ? 'Filtrar por organización' : 'Filter by organization'}
          style={selStyle}
        >
          <option value="">{lang === 'es' ? 'Todas las organizaciones' : 'All organizations'}</option>
          {orgs.map((o) => (
            <option key={o.OrgId} value={o.OrgId}>{o.OrgName}</option>
          ))}
        </select>
        <select
          value={siteId}
          onChange={(e) => { setSiteId(e.target.value); table.setPage(1); }}
          aria-label={lang === 'es' ? 'Filtrar por site' : 'Filter by site'}
          style={selStyle}
        >
          <option value="">{lang === 'es' ? 'Todos los sites' : 'All sites'}</option>
          {sitesForFilter.map((s) => (
            <option key={s.SiteId} value={s.SiteId}>{s.SiteName}</option>
          ))}
        </select>
        <select
          value={roleF}
          onChange={(e) => { setRoleF(e.target.value); table.setPage(1); }}
          aria-label={lang === 'es' ? 'Filtrar por rol' : 'Filter by role'}
          style={selStyle}
        >
          <option value="">{lang === 'es' ? 'Todos los roles' : 'All roles'}</option>
          {ROLE_LIST.map((r) => (
            <option key={r} value={r}>{t('roles.' + r)}</option>
          ))}
        </select>
        {q || orgId || siteId || roleF ? (
          <button
            className="btn sm ghost"
            onClick={() => { setQ(''); setOrgId(''); setSiteId(''); setRoleF(''); table.setPage(1); }}
          >
            {ic.close} {lang === 'es' ? 'Limpiar' : 'Clear'}
          </button>
        ) : null}
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        empty={lang === 'es' ? 'Sin usuarios para estos filtros' : 'No users for these filters'}
        sort={table.sort}
        onSort={table.onSort}
        stickyHeader
        onRowClick={(u) => setEditing(u)}
      />
      <Pagination page={page} pageCount={pageCount} onPage={table.setPage} total={all.length} per={table.per} onPer={table.onPer} />

      <UserEditor
        open={editing !== null}
        user={editing}
        saving={saving}
        onClose={() => !saving && setEditing(null)}
        onSave={save}
      />
    </div>
  );
}
